/**
 * Users API slice (lightweight user tracking + onboarding).
 * Leaf module (Perf 3.1.b/e Step 6). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const usersApi = {
  updateNotificationPreferences: (preferences, isRequestCurrent) => fetchWithAuth('/json/user/notification-preferences', {
    method: 'POST', body: preferences, isRequestCurrent,
  }),
  trackViewedCreditsAutoTopup: async () => {
    try {
      await fetchWithAuth('/json/user/track-viewed-credits-auto-topup', { method: 'POST' });
    } catch (_) {}
  },

  trackViewedCancelPlusPage: async () => {
    try {
      await fetchWithAuth('/json/user/track-viewed-cancel-plus-page', { method: 'POST' });
    } catch (_) {}
  },

  // Real checkout funnel events, per processor. Fire-and-forget: analytics must
  // never be able to interrupt a purchase. `keepalive` lets the abandonment
  // event survive the page unload that caused it — the payload is a few dozen
  // bytes, far inside the 64KB keepalive budget.
  trackCheckoutEvent: async ({ event, processor, checkoutType, reason } = {}) => {
    try {
      await fetchWithAuth('/json/user/track-checkout-event', {
        method: 'POST',
        keepalive: true,
        body: { event, processor, checkoutType, reason },
      });
    } catch (_) {}
  },

  trackOnboarding: async (onboardingData) => {
    const data = await fetchWithAuth('/json/user/track-onboarding', {
      method: 'POST',
      body: onboardingData || {},
    });

    if (data.error) {
      if (data.message === 'Onboarding already completed') {
        return data;
      }
      throw new ApiError(data.message || 'Failed to track onboarding', 400, data);
    }

    return data;
  },

  registerPushToken: async ({ token, platform, isRequestCurrent }) => {
    const data = await fetchWithAuth('/json/user/push-token', {
      method: 'POST',
      body: { token, platform },
      isRequestCurrent,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to register push token', 400, data);
    }
    return data;
  },

  unregisterPushToken: async (token) => {
    const data = await fetchWithAuth('/json/user/push-token', {
      method: 'DELETE',
      body: { token },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to unregister push token', 400, data);
    }
    return data;
  },
};
